import { Routes, Route, Navigate } from 'react-router-dom';
import ScrollManager from '@/components/ScrollManager';
import LandingPage from '@/pages/LandingPage';
import InActionPage from '@/pages/InActionPage';
import AuthPage from '@/pages/AuthPage';
import AuthCallback from '@/pages/AuthCallback';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import Dashboard from '@/pages/Dashboard';
import ResumeBuilder from '@/pages/ResumeBuilder';
import ProtectedRoute from '@/components/ProtectedRoute';
import EmployerRegister from '@/pages/EmployerRegister';
import JobBoard from '@/pages/JobBoard';
import JobDetails from '@/pages/JobDetails';
import RegionalAdminDashboard from '@/pages/RegionalAdminDashboard';
import EmployerDashboard from '@/pages/EmployerDashboard';
import EmployerJobs from '@/pages/EmployerJobs';
import PostJob from '@/pages/PostJob';
import MessagingPage from '@/pages/MessagingPage';
import CandidateSearch from '@/pages/CandidateSearch';
import HomepageContentEditor from '@/pages/HomepageContentEditor';
import SkillBridgeSyncAdmin from '@/pages/SkillBridgeSyncAdmin';
import AdminUserDetail from '@/pages/admin/AdminUserDetail';
import MatchV2Diagnostics from '@/pages/admin/MatchV2Diagnostics';
import EmployerSettings from '@/pages/EmployerSettings';
import RegionalAdminSettings from '@/pages/RegionalAdminSettings';
import EmployerNews from '@/pages/EmployerNews';
import EmployerNewsArticle from '@/pages/EmployerNewsArticle';
import EmployerResources from '@/pages/EmployerResources';
import PlatformArticle from '@/pages/PlatformArticle';
import PlatformContent from '@/pages/admin/PlatformContent';
import RegionalAdminNews from '@/pages/RegionalAdminNews';
import RegionalAdminResources from '@/pages/RegionalAdminResources';
import VisibilityPage from '@/pages/VisibilityPage';
import InterviewReadiness from '@/pages/InterviewReadiness';
import ApplicationWorkspace from '@/pages/ApplicationWorkspace';
import PartnerInquiry from '@/pages/PartnerInquiry';
import { TermsPage, PrivacyPage } from '@/pages/LegalPage';
import AboutPage from '@/pages/public/AboutPage';
import ContactPage from '@/pages/public/ContactPage';
import NewsResearchList from '@/pages/public/NewsResearchList';
import NewsResearchDetail from '@/pages/public/NewsResearchDetail';
import PublicContent from '@/pages/admin/PublicContent';

function AppRouter() {
  return (
    <>
      <ScrollManager />
      <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/in-action" element={<InActionPage />} />
      <Route path="/partner" element={<PartnerInquiry />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/news-research" element={<NewsResearchList />} />
      <Route path="/news-research/:slug" element={<NewsResearchDetail />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

      {/* /dashboard is the Job Seeker "Transition" home (admin also uses it). Employers and
          regional admins are redirected to their own role home by ProtectedRoute. */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['job_seeker', 'admin']}>
          <Dashboard />
        </ProtectedRoute>
      } />

      <Route path="/profile/visibility" element={
        <ProtectedRoute allowedRoles={['job_seeker', 'admin']}>
          <VisibilityPage />
        </ProtectedRoute>
      } />

      <Route path="/resume-builder" element={
        <ProtectedRoute allowedRoles={['job_seeker', 'admin']}>
          <ResumeBuilder />
        </ProtectedRoute>
      } />

      <Route path="/interview-readiness" element={
        <ProtectedRoute allowedRoles={['job_seeker', 'admin']}>
          <InterviewReadiness />
        </ProtectedRoute>
      } />

      <Route path="/applications/:applicationId" element={
        <ProtectedRoute allowedRoles={['job_seeker', 'employer', 'admin']}>
          <ApplicationWorkspace />
        </ProtectedRoute>
      } />

      <Route path="/cover-letter" element={<Navigate to="/resume-builder" replace />} />
      <Route path="/invites" element={<Navigate to="/messages" replace />} />

      <Route path="/employer/register" element={<ProtectedRoute><EmployerRegister /></ProtectedRoute>} />
      <Route path="/employer/dashboard" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <EmployerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/employer/jobs" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <EmployerJobs />
        </ProtectedRoute>
      } />
      <Route path="/jobs/post" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <PostJob />
        </ProtectedRoute>
      } />
      <Route path="/candidates/search" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <CandidateSearch />
        </ProtectedRoute>
      } />
      <Route path="/employer/news" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <EmployerNews />
        </ProtectedRoute>
      } />
      <Route path="/employer/news/:newsId" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <EmployerNewsArticle />
        </ProtectedRoute>
      } />
      <Route path="/employer/resources" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <EmployerResources />
        </ProtectedRoute>
      } />
      <Route path="/employer/article/:contentType/:contentId" element={
        <ProtectedRoute allowedRoles={['employer', 'admin']}>
          <PlatformArticle />
        </ProtectedRoute>
      } />

      {/* Opportunities is the personalized Job Seeker experience (map, match, resume prompts) —
          not public and not for employers. Employers/regional admins are redirected to their role
          home by ProtectedRoute. The job DETAIL page stays shared/public (sharable links). */}
      <Route path="/jobs" element={
        <ProtectedRoute allowedRoles={['job_seeker', 'admin']}>
          <JobBoard />
        </ProtectedRoute>
      } />
      <Route path="/jobs/:jobId" element={<JobDetails />} />

      <Route path="/messages" element={
        <ProtectedRoute allowedRoles={['job_seeker', 'employer', 'regional_admin', 'admin']}>
          <MessagingPage />
        </ProtectedRoute>
      } />

      <Route path="/regional-admin" element={
        <ProtectedRoute allowedRoles={['regional_admin', 'admin']}>
          <RegionalAdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/regional-admin/news" element={
        <ProtectedRoute allowedRoles={['regional_admin', 'admin']}>
          <RegionalAdminNews />
        </ProtectedRoute>
      } />
      <Route path="/regional-admin/resources" element={
        <ProtectedRoute allowedRoles={['regional_admin', 'admin']}>
          <RegionalAdminResources />
        </ProtectedRoute>
      } />

      <Route path="/admin/homepage-content" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <HomepageContentEditor />
        </ProtectedRoute>
      } />

      <Route path="/admin/content" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <PlatformContent />
        </ProtectedRoute>
      } />

      <Route path="/admin/public-content" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <PublicContent />
        </ProtectedRoute>
      } />

      <Route path="/admin/skillbridge-sync" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <SkillBridgeSyncAdmin />
        </ProtectedRoute>
      } />

      <Route path="/admin/users/:userId" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminUserDetail />
        </ProtectedRoute>
      } />
      <Route path="/admin/match-v2-diagnostics" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <MatchV2Diagnostics />
        </ProtectedRoute>
      } />

      <Route path="/employer/settings" element={
        <ProtectedRoute allowedRoles={['employer']}>
          <EmployerSettings />
        </ProtectedRoute>
      } />

      <Route path="/regional-admin/settings" element={
        <ProtectedRoute allowedRoles={['regional_admin', 'admin']}>
          <RegionalAdminSettings />
        </ProtectedRoute>
      } />
      </Routes>
    </>
  );
}

export default AppRouter;
